class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initScrollCue();
  }

  // — Scroll-vangnet —
  // De Section Rendering API vervangt hier hele takken (cart-drawer-items via
  // onCartUpdate, de complete .drawer__inner via renderContents). Alles wat aan
  // die takken hangt is dus vluchtig; daarom luisteren we op <cart-drawer>
  // zelf, dat nooit vervangen wordt.
  initScrollCue() {
    this.scrollCueFrame = null;
    // scroll bubbelt niet, maar loopt wel door de capture-fase van de
    // voorouders — vandaar `true`. Zo overleeft de listener elke re-render.
    this.addEventListener('scroll', () => this.scheduleScrollCue(), true);
    window.addEventListener('resize', () => this.scheduleScrollCue());

    const contents = this.querySelector('#CartDrawer');
    if (contents && typeof MutationObserver !== 'undefined') {
      // Alleen childList/subtree: attributen bewust niet, anders zou onze eigen
      // class-toggle op de chip de observer opnieuw laten vuren.
      this.contentsObserver = new MutationObserver(() => this.scheduleScrollCue());
      this.contentsObserver.observe(contents, { childList: true, subtree: true });
    }

    this.scheduleScrollCue();
  }

  scheduleScrollCue() {
    if (this.scrollCueFrame) return;
    this.scrollCueFrame = requestAnimationFrame(() => {
      this.scrollCueFrame = null;
      this.updateScrollCue();
    });
  }

  updateScrollCue() {
    const cue = this.querySelector('[data-fp-scroll-cue]');
    if (!cue) return;

    const scroller = this.querySelector('cart-drawer-items');
    let show = false;

    // Onder 650px schermhoogte zet Dawn cart-drawer-items op overflow: visible
    // en scrollt .drawer__inner. De fade hoort dan niet bij het scrollvenster,
    // dus laten we hem in dat geval weg.
    if (scroller && getComputedStyle(scroller).overflowY !== 'visible') {
      const scrollable = scroller.scrollHeight - scroller.clientHeight;
      show = scrollable > 4 && scrollable - scroller.scrollTop > 16;
    }

    cue.classList.toggle('is-visible', show);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    this.scheduleScrollCue();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.scheduleScrollCue();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
